import { useEffect, useRef, useState } from 'react'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '~/components/UI/shadcn/tabs'
import { cn } from '~/lib/utils'

interface TabItem {
  id: number
  header: string
  content: React.ReactNode
}

interface TabCompProps {
  tabs: TabItem[]
  layoutColour?: AllBackgroundColours
}

const TabComp = ({ tabs = [], layoutColour }: TabCompProps) => {
  // set and store active tab on click
  const [activeTab, setActiveTab] = useState<number>(0)
  // store tab details in an array
  const tabRef = useRef<Array<HTMLButtonElement | null>>([])
  // store svg details
  const borderRef = useRef<SVGRectElement | null>(null)

  const updateBorderPosition = () => {
    // get details of currently active tab
    const currentTab = tabRef.current[activeTab]

    if (currentTab && borderRef.current) {
      // allows useage of offsets within the active tab
      const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = currentTab
      const topPos = offsetTop + offsetHeight

      // updates position on the x axis and width of the svg element
      borderRef.current.setAttribute('x', offsetLeft.toString())
      borderRef.current.setAttribute('y', (topPos - 5).toString())
      borderRef.current.setAttribute('width', offsetWidth.toString())
    }
  }

  useEffect(() => {
    // init update border function
    updateBorderPosition()

    const handleResize = () => {
      // smoother animation and avoids excessive repaints
      requestAnimationFrame(updateBorderPosition)
    }

    // used for dynamic/responsive layout adjustments, if this stays depends on how tabs are handled in different screen sizes
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const isDarkBg = ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
  const svgColour = isDarkBg ? 'fill-white' : 'fill-blue-d'
  const textColour = isDarkBg ? 'border-b-white' : 'border-b-blue-d'
  const shadowColour = isDarkBg
    ? 'hover:shadow-hover-sm-light data-[state=active]:hover:shadow-hover-sm-light'
    : 'hover:shadow-hover-sm-dark data-[state=active]:hover:shadow-hover-sm-dark'

  if (!tabs.length) return null

  return (
    <div>
      <Tabs defaultValue={tabs[0].header.toLowerCase()} className=''>
        <TabsList className='relative h-full flex-wrap justify-start rounded-none bg-transparent p-0 dark:border-b-white dark:bg-transparent'>
          {tabs.map((tab, index) => (
            <TabsTrigger
              key={tab.id}
              ref={el => {
                tabRef.current[index] = el
              }}
              onFocus={() => setActiveTab(index)}
              className={cn(
                'text-blue-white px-xs py-xs z-1 rounded-none border-b-2 leading-[1.15rem] font-normal tracking-widest uppercase transition-shadow duration-300 data-[state=active]:bg-transparent data-[state=active]:font-bold dark:hover:shadow-white dark:data-[state=active]:bg-transparent dark:data-[state=active]:text-white',
                textColour,
                shadowColour
              )}
              value={tab.header.toLowerCase()}
            >
              {tab.header}
            </TabsTrigger>
          ))}

          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='absolute bottom-[1px] h-full w-full overflow-visible'
          >
            <rect
              ref={borderRef}
              x='0'
              y='0'
              width='0'
              className={`h-[6px] transition-all duration-300 ${svgColour}`}
            />
          </svg>
        </TabsList>

        {tabs.map(tab => (
          <TabsContent
            key={tab.id}
            value={tab.header.toLowerCase()}
            className={'py-xs'}
          >
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

export default TabComp
